import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@components/react/pricing/lib/utils"

// Update the button variants to use pill shape (rounded-full) for CTAs
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[#ef483d] text-primary-foreground hover:bg-[#d43d33] rounded-full",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-md",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-full",
        secondary: "border border-[#cdcece80] bg-transparent text-[#000000] hover:bg-gray-50 rounded-full",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-md",
        link: "text-primary underline-offset-4 hover:underline rounded-md",
        mint: "bg-[#25c5a1] text-white hover:bg-[#20b090] rounded-full",
        sunrise: "bg-[#b99532] text-white hover:bg-[#a58529] rounded-full",
      },
      size: {
        default: "h-10 px-6 py-2", // Increased horizontal padding for pill shape
        sm: "h-9 px-5 py-2", // Increased horizontal padding for pill shape
        lg: "h-11 px-8 py-2",
        icon: "h-10 w-10 rounded-full", // Icon buttons are already circular
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
